import {
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ExternalLink } from 'react-feather';
import { Link } from 'react-router-dom';
import { columnOf } from '../model/review';
import type {
  MatchingSessionState,
  RunTask,
  RunTaskStatus,
} from '../model/types';

const STATUS: Record<RunTaskStatus, { label: string; color: string }> = {
  running: { label: 'Running', color: 'blue' },
  found: { label: 'Matching found', color: 'medleyGreen' },
  dead_end: { label: 'No matching', color: 'gray' },
  error: { label: 'Error', color: 'dangerRed' },
  stopped: { label: 'Stopped', color: 'gray' },
  idle: { label: 'Waiting', color: 'gray' },
};

interface MatchingProgressProps {
  session: MatchingSessionState;
  cohortId: string;
  /** Opens the results editor. Omit to hide the button. */
  onReviewResults?: () => void;
  reviewingResults?: boolean;
  onViewSetup: () => void;
}

function taskLabel(task: RunTask, session: MatchingSessionState): string {
  const goals = task.taskTypes
    .filter(t => t !== 'sz')
    .map(t => columnOf(session, t)?.label ?? t);
  return goals.length === 0
    ? 'Group requirements only'
    : `Requirements + ${goals.join(' + ')}`;
}

export function MatchingProgress({
  session,
  cohortId,
  onReviewResults,
  reviewingResults,
  onViewSetup,
}: MatchingProgressProps) {
  const tasks = Object.values(session.tasks).sort(
    (a, b) =>
      a.taskTypes.length - b.taskTypes.length || a.pid.localeCompare(b.pid)
  );
  const count = (s: RunTaskStatus) => tasks.filter(t => t.status === s).length;
  const running = count('running') + count('idle');
  const found = count('found');
  const deadEnds = count('dead_end');
  const errors = count('error');
  const name = session.config?.request.name ?? 'This request';

  let headline: string;
  if (tasks.length === 0) headline = `${name} is starting…`;
  else if (running > 0) headline = `${name} is running`;
  else if (found > 0)
    headline = `${name} found ${found} ${found === 1 ? 'matching' : 'matchings'}`;
  else headline = `${name} finished without a matching`;

  return (
    <Stack gap="lg">
      <Card withBorder p={0} radius="lg">
        <Box px={28} pt={24} pb={18}>
          <Group gap="sm">
            {(running > 0 || tasks.length === 0) && <Loader size="sm" />}
            <Title order={2} c="medleyGreen.8">
              {headline}
            </Title>
          </Group>
          <Text c="dimmed" mt={6}>
            Matching tries each combination of your matching goals on top of
            your group requirements. You can leave this page and come back.
          </Text>
        </Box>
        <Divider />
        <SimpleGrid cols={{ base: 2, sm: 4 }} px={28} py="lg">
          {[
            { label: 'Running', value: running },
            { label: 'Matchings found', value: found },
            { label: 'No matching', value: deadEnds },
            { label: 'Errors', value: errors },
          ].map(s => (
            <Paper key={s.label} withBorder radius="md" p="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                {s.label}
              </Text>
              <Text fz={28} fw={700}>
                {s.value}
              </Text>
            </Paper>
          ))}
        </SimpleGrid>
        {tasks.length > 0 && (
          <Stack gap={0} px={28} pb="lg">
            {tasks.map(t => (
              <Group
                key={t.pid}
                justify="space-between"
                py="sm"
                style={{ borderTop: '1px solid var(--mantine-color-gray-2)' }}
              >
                <Box>
                  <Text size="sm" fw={500}>
                    {taskLabel(t, session)}
                  </Text>
                  {t.error && (
                    <Text size="xs" c="dangerRed.7">
                      {t.error}
                    </Text>
                  )}
                </Box>
                <Badge color={STATUS[t.status].color} variant="light">
                  {STATUS[t.status].label}
                </Badge>
              </Group>
            ))}
          </Stack>
        )}
      </Card>
      <Group justify="space-between">
        <Group gap="sm">
          <Button
            variant="default"
            component={Link}
            to={`/cohorts/${cohortId}`}
          >
            Back to cohort groups
          </Button>
          <Button variant="subtle" onClick={onViewSetup}>
            View setup
          </Button>
        </Group>
        {onReviewResults && (
          <Button
            rightSection={<ExternalLink size={16} />}
            disabled={found === 0}
            loading={reviewingResults}
            onClick={onReviewResults}
          >
            Review results
          </Button>
        )}
      </Group>
    </Stack>
  );
}
